/**
 * Timesheets WRITE endpoints — request bodies, wire types and coded errors for
 * the routes in apps/api/src/routes/projects/timesheets.ts (mounted at
 * `/api/v1/timesheets`):
 *
 *   POST   /orgs/:orgId/timesheets                         createTimesheetSchema
 *   PATCH  /orgs/:orgId/timesheets/:timesheetId            updateTimesheetSchema
 *   POST   /orgs/:orgId/timesheets/:timesheetId/entries/save  saveTimeEntriesSchema
 *   POST   /orgs/:orgId/time-clock/clock-in                clockInSchema
 *   POST   /orgs/:orgId/time-clock/:entryId/clock-out      clockOutSchema
 *
 * Single-row writes are Pattern B on the caller's user client (RLS is the
 * permission layer; `created_by` is the auth user and the INSERT policies
 * enforce it). The multi-row save is Pattern B+ through the SECURITY INVOKER
 * `save_time_entries` RPC. Every body is `.strict()`: an unknown key — notably
 * `organization_id`, `created_by` or `timesheet_id` on an entry — is a 400.
 *
 * WALL CLOCK (docs/DATES.md "Venue wall-clock values"). Every time on the
 * wire IN is `'HH:mm'` (`localTimeSchema`), stamped by the browser's own
 * clock — never the server's, never the org timezone. The `time` columns come
 * back from Postgres as `'HH:mm:ss'`. Dates are calendar days
 * (`calendarDateSchema`).
 *
 * Shared by the API (validation) and the web hooks (payload types) so the two
 * cannot drift.
 */
import { z } from "zod";
import { calendarDateSchema, localTimeSchema } from "./dateWindow.js";
// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------
/** `''` / whitespace-only → `null`; anything else passes through untouched. */
const blankToNull = (value) => typeof value === "string" && value.trim() === "" ? null : value;
/** Nullable free text; `''` clears the column. */
const nullableText = (max) => z.preprocess(blankToNull, z.string().trim().max(max).nullable());
/** Nullable uuid; `''` clears the column. */
const nullableUuid = z.preprocess(blankToNull, z.string().uuid().nullable());
const AT_LEAST_ONE_FIELD = "At least one field must be provided";
/** superRefine for PATCH bodies: an empty object is a 400, not a no-op 200. */
function refineNonEmpty(value, ctx) {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: AT_LEAST_ONE_FIELD });
    }
}
// ---------------------------------------------------------------------------
// Time entries — ONE field list
// ---------------------------------------------------------------------------
/**
 * The writable `time_entries` columns — the ONE list. The create / update
 * schemas below derive from it, and it MIRRORS the key list the
 * `save_time_entries` RPC reads with `item ? '<key>'` (the migration's body
 * points back here). Adding a writable column means adding it in BOTH places.
 * `timesheet_id`, `organization_id` and `created_by` are deliberately absent:
 * the RPC / route derive them, and the immutable-columns trigger refuses a
 * change to them.
 */
export const TIME_ENTRY_FIELDS = [
    "project_id",
    "person_id",
    "business_id",
    "task",
    "entry_date",
    "start_time",
    "end_time",
    "is_estimated",
    "is_billable",
    "note",
];
/**
 * One schema per field. `end_time` is nullable HERE on purpose: whether a NULL
 * end is allowed is the DATABASE's rule (`save_time_entries` refuses a create
 * without one, or an update that sets it NULL — 400 `timesheet_end_time_required`;
 * only clock-in opens an entry), so it lives in one place.
 */
const TIME_ENTRY_FIELD_SCHEMAS = {
    project_id: nullableUuid,
    person_id: nullableUuid,
    business_id: nullableUuid,
    task: nullableText(500),
    entry_date: calendarDateSchema,
    start_time: localTimeSchema,
    end_time: localTimeSchema.nullable(),
    is_estimated: z.boolean(),
    is_billable: z.boolean(),
    note: nullableText(5000),
};
/** Every field optional — the base both entry schemas spread. */
const optionalTimeEntryShape = Object.fromEntries(TIME_ENTRY_FIELDS.map((f) => [f, TIME_ENTRY_FIELD_SCHEMAS[f].optional()]));
/**
 * One new entry in a save batch: `entry_date` and `start_time` required, the
 * rest optional (an omitted `is_billable` defaults to true, as clock-in).
 */
export const timeEntryCreateSchema = z
    .object({
    ...optionalTimeEntryShape,
    entry_date: TIME_ENTRY_FIELD_SCHEMAS.entry_date,
    start_time: TIME_ENTRY_FIELD_SCHEMAS.start_time,
})
    .strict();
/**
 * One changed entry in a save batch: `id` plus ONLY the changed keys (a
 * present key is written, JSON `null` clears a nullable column, an absent key
 * is left alone — the RPC's `item ? key` semantics).
 */
export const timeEntryUpdateSchema = z
    .object({ id: z.string().uuid(), ...optionalTimeEntryShape })
    .strict();
/** Upper bound per array in one save — a sanity cap, far above a grid edit. */
export const TIME_ENTRIES_SAVE_MAX = 2000;
/**
 * POST …/timesheets/:timesheetId/entries/save — one atomic batch
 * (`save_time_entries`: delete → insert → partial update). Every array is
 * optional and defaults to empty; at least one must be non-empty.
 */
export const saveTimeEntriesSchema = z
    .object({
    creates: z.array(timeEntryCreateSchema).max(TIME_ENTRIES_SAVE_MAX).default([]),
    updates: z.array(timeEntryUpdateSchema).max(TIME_ENTRIES_SAVE_MAX).default([]),
    deleteIds: z.array(z.string().uuid()).max(TIME_ENTRIES_SAVE_MAX).default([]),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.creates.length + v.updates.length + v.deleteIds.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Nothing to save" });
    }
});
// ---------------------------------------------------------------------------
// Time clock
// ---------------------------------------------------------------------------
/**
 * POST …/time-clock/clock-in — opens an entry (`end_time` NULL) on the given
 * timesheet for the caller. `entry_date` / `start_time` are the browser's
 * local wall clock. One running clock per user per org
 * (`time_entries_one_open_per_user`) → 409 `timesheet_already_clocked_in`.
 */
export const clockInSchema = z
    .object({
    timesheetId: z.string().uuid(),
    entry_date: TIME_ENTRY_FIELD_SCHEMAS.entry_date,
    start_time: TIME_ENTRY_FIELD_SCHEMAS.start_time,
    task: TIME_ENTRY_FIELD_SCHEMAS.task.optional(),
    project_id: TIME_ENTRY_FIELD_SCHEMAS.project_id.optional(),
    person_id: TIME_ENTRY_FIELD_SCHEMAS.person_id.optional(),
    business_id: TIME_ENTRY_FIELD_SCHEMAS.business_id.optional(),
    note: TIME_ENTRY_FIELD_SCHEMAS.note.optional(),
    is_billable: TIME_ENTRY_FIELD_SCHEMAS.is_billable.optional(),
})
    .strict();
/** POST …/time-clock/:entryId/clock-out — closes the caller's running entry. */
export const clockOutSchema = z.object({ end_time: localTimeSchema }).strict();
// ---------------------------------------------------------------------------
// Timesheets
// ---------------------------------------------------------------------------
/** `timesheets.visibility` — CHECK-constrained to these three tiers. */
export const TIMESHEET_VISIBILITIES = ["members", "admins", "private"];
const timesheetOptionalFields = {
    description: nullableText(5000).optional(),
    column_config: z.record(z.string(), z.boolean()).optional(),
    column_order: z.array(z.string().min(1).max(100)).max(100).optional(),
    project_id: nullableUuid.optional(),
    business_id: nullableUuid.optional(),
    person_id: nullableUuid.optional(),
    visibility: z.enum(TIMESHEET_VISIBILITIES).optional(),
};
const timesheetName = z.string().trim().min(1).max(255);
/**
 * POST /orgs/:orgId/timesheets. Omitted keys take the DB defaults (the web
 * dialog sends its own column defaults). `visibility: 'admins'` from a
 * non-admin → 403 `timesheet_admins_visibility_forbidden` (the row could not
 * be read back through `timesheets_select`).
 */
export const createTimesheetSchema = z
    .object({ name: timesheetName, ...timesheetOptionalFields })
    .strict();
/** PATCH /orgs/:orgId/timesheets/:timesheetId — any subset, at least one key. */
export const updateTimesheetSchema = z
    .object({ name: timesheetName.optional(), ...timesheetOptionalFields })
    .strict()
    .superRefine(refineNonEmpty);
// ---------------------------------------------------------------------------
// Coded errors — `{ error, code }`, read by the web as `err.envelope?.code`
// ---------------------------------------------------------------------------
export const TIMESHEET_ERROR_CODES = {
    /** 409 — `time_entries_one_open_per_user`: the caller already has a running clock in this org. */
    alreadyClockedIn: "timesheet_already_clocked_in",
    /** 409 — `TIMESHEET_HAS_OTHERS_ENTRIES`: deleting would remove other people's hours (org admins may). */
    hasOthersEntries: "timesheet_has_others_entries",
    /** 400 — `TIME_ENTRY_END_REQUIRED`: a saved entry needs an end time (only clock-in opens one). */
    endTimeRequired: "timesheet_end_time_required",
    /** 409 — `TIME_ENTRIES_CHANGED`: some rows were changed or removed meanwhile (or are not yours) — reload. */
    entriesChanged: "timesheet_entries_changed",
    /** 403 — a non-admin asked for an `admins` timesheet. */
    adminsVisibilityForbidden: "timesheet_admins_visibility_forbidden",
};
//# sourceMappingURL=timesheetsWrites.js.map